import { CircularProgress } from "@mui/material";
import type { TFunction } from "i18next";
import DonutChart, { DonutChartSegment } from "@/components/dataVisualization/donutChart";
import MetricBreakdownTable from "@/components/dataVisualization/metricBreakdownTable";
import styles from "@/components/lms/lmsStatusOverviewCard.module.css";

export type LmsStatusOverviewViewMode = "chart" | "table";

export interface LmsStatusOverviewStatusItem {
  key: string;
  label: string;
  count: number;
  percent: number;
  color: string;
}

interface Props {
  chartSegments: DonutChartSegment[];
  dualDisplayMode: boolean;
  integerFormatter: Intl.NumberFormat;
  isLoadingOverview: boolean;
  overviewError: any;
  percentFormatter: Intl.NumberFormat;
  statusItems: LmsStatusOverviewStatusItem[];
  t: TFunction;
  totalCount: number;
  viewMode: LmsStatusOverviewViewMode;
}

function LmsStatusOverviewChart({
  chartSegments,
  integerFormatter,
  percentFormatter,
  statusItems,
  t,
}: Pick<
  Props,
  "chartSegments" | "integerFormatter" | "percentFormatter" | "statusItems" | "t"
>) {
  return (
    <div className={styles.chartContainer}>
      <DonutChart
        segments={chartSegments}
        ariaLabel={t("status")}
        valueFormatter={(value) => integerFormatter.format(value)}
      />
      <div className={styles.legend}>
        {statusItems.map((item) => (
          <div key={item.key} className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: item.color }}
            />
            <span className={styles.legendText}>
              {`${item.label}: ${integerFormatter.format(item.count)} (${percentFormatter.format(item.percent)}%)`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LmsStatusOverviewTable({
  integerFormatter,
  percentFormatter,
  statusItems,
  t,
  totalCount,
}: Pick<
  Props,
  "integerFormatter" | "percentFormatter" | "statusItems" | "t" | "totalCount"
>) {
  return (
    <MetricBreakdownTable
      rows={statusItems.map((item) => ({
        id: item.key,
        label: item.label,
        value: item.count,
        percentage: item.percent,
        color: item.color,
      }))}
      labelHeader={t("progress")}
      valueHeader={t("enrolments")}
      percentageHeader={t("percent")}
      totalLabel={t("total")}
      totalValue={totalCount}
      totalPercentage={totalCount > 0 ? 100 : 0}
      valueFormatter={(value) => integerFormatter.format(value)}
      percentageFormatter={(value) => `${percentFormatter.format(value)}%`}
    />
  );
}

export default function LmsStatusOverviewDisplay({
  chartSegments,
  dualDisplayMode,
  integerFormatter,
  isLoadingOverview,
  overviewError,
  percentFormatter,
  statusItems,
  t,
  totalCount,
  viewMode,
}: Props) {
  if (isLoadingOverview) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress color="secondary" />
      </div>
    );
  }

  if (overviewError) {
    return <div className={styles.errorText}>{t("error_occurred")}</div>;
  }

  const chart = (
    <LmsStatusOverviewChart
      chartSegments={chartSegments}
      integerFormatter={integerFormatter}
      percentFormatter={percentFormatter}
      statusItems={statusItems}
      t={t}
    />
  );
  const table = (
    <LmsStatusOverviewTable
      integerFormatter={integerFormatter}
      percentFormatter={percentFormatter}
      statusItems={statusItems}
      t={t}
      totalCount={totalCount}
    />
  );

  if (dualDisplayMode) {
    return (
      <div className={styles.dualDisplayLayout}>
        {chart}
        <div className={styles.tableContainer}>{table}</div>
      </div>
    );
  }

  return viewMode === "chart" ? chart : table;
}
